import { useState, useEffect, useRef, useCallback } from 'react'

export interface PracticeEvent {
  type: 'scenario_update' | 'response_update' | 'end' | 'heartbeat'
  practice_id: string
  convention_id: string
  payload: {
    scenario_id?: string
    scenario_data?: unknown
    response_count?: number
  }
  timestamp: string
}

export interface PracticeSessionState {
  sessionId: string
  scenarioId: string | null
  scenarioData: unknown | null
  responseCount: number
  status: 'active' | 'ended'
  connected: boolean
  error: string | null
}

export function usePracticeSession(sessionId: string | null) {
  const [state, setState] = useState<PracticeSessionState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [connected, setConnected] = useState(false)
  const eventSourceRef = useRef<EventSource | null>(null)

  const closeConnection = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    setConnected(false)
  }, [])

  useEffect(() => {
    if (!sessionId) {
      closeConnection()
      setState(null)
      return
    }

    const API_URL = import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? 'http://localhost:3001' : '')
    const es = new EventSource(`${API_URL}/api/practice/sessions/${sessionId}/events`, {
      withCredentials: true,
    })
    eventSourceRef.current = es

    es.addEventListener('open', () => {
      setConnected(true)
      setError(null)
    })

    es.addEventListener('scenario_update', (e) => {
      try {
        const data: PracticeEvent = JSON.parse(e.data)
        setState((prev) => ({
          sessionId: data.practice_id,
          scenarioId: data.payload.scenario_id ?? null,
          scenarioData: data.payload.scenario_data ?? null,
          responseCount: prev?.responseCount ?? 0,
          status: data.type === 'end' ? 'ended' : 'active',
          connected: true,
          error: null,
        }))
      } catch (err) {
        setError('Failed to parse scenario update')
      }
    })

    es.addEventListener('response_update', (e) => {
      try {
        const data: PracticeEvent = JSON.parse(e.data)
        setState((prev) =>
          prev
            ? { ...prev, responseCount: data.payload.response_count ?? prev.responseCount }
            : null
        )
      } catch (err) {
        console.error('Failed to parse response update:', err)
      }
    })

    es.addEventListener('end', () => {
      setState((prev) =>
        prev
          ? { ...prev, status: 'ended', connected: false }
          : { sessionId, scenarioId: null, scenarioData: null, responseCount: 0, status: 'ended', connected: false, error: null }
      )
      closeConnection()
    })

    es.addEventListener('error', () => {
      if (es.readyState === EventSource.CLOSED) {
        setConnected(false)
        setError('Connection closed. Session may have ended.')
      } else {
        setError('Connection lost. Reconnecting…')
      }
    })

    return () => {
      es.close()
      eventSourceRef.current = null
    }
  }, [sessionId, closeConnection])

  return { state, error, connected, closeConnection }
}
